import { useState, useEffect, useRef } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  GripVertical,
  Trash2,
  RefreshCw,
  Package,
  Clock,
  Hash,
  Factory,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  AlertCircle,
  Pencil,
  Check
} from 'lucide-react';
import { format, addDays, isSameDay, startOfDay, addMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';

const HOUR_WIDTH = 60;
const ROW_HEIGHT = 70;
const MACHINE_LABEL_WIDTH = 200;
const TOTAL_HOURS = 48;
const HOURS = Array.from({ length: TOTAL_HOURS }, (_, i) => i);
const ILOT_HEADER_HEIGHT = 28; // Height of îlot separator headers
const TIMELINE_HEADER_HEIGHT = 40; // Height of the hours header row

// Working hours defaults
const DEFAULT_WORKING_HOURS = {
  morning_start: 8,
  morning_end: 12,
  afternoon_start: 14,
  afternoon_end: 18,
  work_days: [1, 2, 3, 4, 5]
};

export default function ImportPlanningPage() {
  const [importedActions, setImportedActions] = useState([]);
  const [ilots, setIlots] = useState([]);
  const [machines, setMachines] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, field }
  const [editValue, setEditValue] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [workingHours, setWorkingHours] = useState(DEFAULT_WORKING_HOURS);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlanned, setShowPlanned] = useState('all'); // 'all', 'pending', 'planned'

  // Drag state
  const [draggingAction, setDraggingAction] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [dragPreviewTime, setDragPreviewTime] = useState(null);

  const fileInputRef = useRef(null);
  const timelineRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ilotsRes, machinesRes, actionsRes, batchesRes, hoursRes] = await Promise.all([
        fetch(`${API}/ilots`, { credentials: 'include' }),
        fetch(`${API}/machines`, { credentials: 'include' }),
        fetch(`${API}/imported-actions`, { credentials: 'include' }),
        fetch(`${API}/imported-actions/batches`, { credentials: 'include' }),
        fetch(`${API}/working-hours`, { credentials: 'include' })
      ]);

      if (ilotsRes.ok) setIlots(await ilotsRes.json());
      if (machinesRes.ok) setMachines(await machinesRes.json());
      if (actionsRes.ok) setImportedActions(await actionsRes.json());
      if (batchesRes.ok) setBatches(await batchesRes.json());
      if (hoursRes.ok) setWorkingHours(await hoursRes.json());
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // First, get preview of data
      const response = await fetch(`${API}/imported-actions/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        if (!result.rows || result.rows.length === 0) {
          toast.error("Aucune donnée trouvée dans le fichier Excel");
          return;
        }
        setPreviewData(result.rows);
        setUploadDialogOpen(false);
        setPreviewDialogOpen(true);
        toast.success(`${result.rows.length} ligne(s) trouvée(s)`);
      } else {
        try {
          const error = await response.json();
          toast.error(error.detail || `Erreur ${response.status}: ${response.statusText}`);
        } catch {
          toast.error(`Erreur ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Erreur réseau lors de la lecture: ${error.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Toggle row selection
  const handleToggleRow = (rowIndex) => {
    setPreviewData(prev => prev.map((row, idx) =>
      idx === rowIndex ? { ...row, selected: !row.selected } : row
    ));
  };

  // Toggle all rows
  const handleToggleAll = () => {
    const allSelected = previewData.every(row => row.selected);
    setPreviewData(prev => prev.map(row => ({ ...row, selected: !allSelected })));
  };

  // Start editing a cell
  const handleStartEdit = (rowIndex, field, currentValue) => {
    setEditingCell({ rowIndex, field });
    setEditValue(currentValue?.toString() || '');
  };

  // Save cell edit
  const handleSaveEdit = () => {
    if (!editingCell) return;

    setPreviewData(prev => prev.map((row, idx) => {
      if (idx === editingCell.rowIndex) {
        let value = editValue;
        // Convert to number for numeric fields
        if (['quantity', 'setup_time', 'production_time'].includes(editingCell.field)) {
          value = parseInt(value) || 0;
        }
        return { ...row, [editingCell.field]: value };
      }
      return row;
    }));
    setEditingCell(null);
    setEditValue('');
  };

  // Cancel cell edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Confirm import with selected/modified data
  const handleConfirmImport = async () => {
    const selectedRows = previewData.filter(row => row.selected);

    if (selectedRows.length === 0) {
      toast.error('Aucune ligne sélectionnée');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(`${API}/imported-actions/import-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          actions: selectedRows.map(row => ({
            of_number: row.of_number,
            etiquette_number: row.etiquette_number,
            action_number: row.action_number,
            article_code: row.article_code,
            quantity: row.quantity,
            section: row.section,
            resource_code: row.resource_code,
            action_code: row.action_code,
            title: row.title,
            scheduled_date: row.scheduled_date,
            setup_time: row.setup_time,
            production_time: row.production_time
          }))
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.imported_count} action(s) importée(s)`);
        if (result.error_count > 0) {
          toast.warning(`${result.error_count} erreur(s) lors de l'import`);
        }
        setPreviewDialogOpen(false);
        setPreviewData([]);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'import");
      }
    } catch (error) {
      toast.error("Erreur réseau lors de l'import");
    } finally {
      setUploading(false);
    }
  };

  // Render editable cell
  const renderEditableCell = (row, rowIndex, field, displayValue) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="h-7 text-xs"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit();
              if (e.key === 'Escape') handleCancelEdit();
            }}
          />
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleSaveEdit}>
            <Check className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleCancelEdit}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      );
    }

    return (
      <div
        className="cursor-pointer hover:bg-muted/50 px-1 py-0.5 rounded flex items-center gap-1 group"
        onClick={() => handleStartEdit(rowIndex, field, row[field])}
      >
        <span className="truncate">{displayValue || '-'}</span>
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
      </div>
    );
  };

  const handleDeleteBatch = async (batchId) => {
    if (!confirm('Supprimer toutes les actions de cet import ?')) return;

    try {
      const response = await fetch(`${API}/imported-actions/batch/${batchId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Import supprimé');
        fetchData();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDeleteAction = async (importedId) => {
    try {
      const response = await fetch(`${API}/imported-actions/${importedId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Action supprimée');
        setImportedActions(prev => prev.filter(a => a.imported_id !== importedId));
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  // Filter imported actions
  const filteredActions = importedActions.filter(action => {
    const matchesBatch = selectedBatch === 'all' || action.import_batch_id === selectedBatch;
    const matchesSearch = searchTerm === '' ||
      action.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.of_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.article_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlanned = showPlanned === 'all' ||
      (showPlanned === 'pending' && !action.is_planned) ||
      (showPlanned === 'planned' && action.is_planned);
    return matchesBatch && matchesSearch && matchesPlanned;
  });

  // Counts for display
  const pendingCount = importedActions.filter(a => !a.is_planned).length;
  const plannedCount = importedActions.filter(a => a.is_planned).length;

  // Group machines by ilot
  const machinesByIlot = ilots.map(ilot => ({
    ilot,
    machines: machines.filter(m => m.ilot_id === ilot.ilot_id)
  })).filter(group => group.machines.length > 0);

  // Timeline helpers
  const getTimelineStart = () => startOfDay(selectedDate);

  const isWithinWorkingHours = (hour, dayOffset) => {
    const dayOfWeek = (selectedDate.getDay() + dayOffset) % 7;
    if (!workingHours.work_days.includes(dayOfWeek)) return false;
    const hourInDay = hour % 24;
    const isMorning = hourInDay >= workingHours.morning_start && hourInDay < workingHours.morning_end;
    const isAfternoon = hourInDay >= workingHours.afternoon_start && hourInDay < workingHours.afternoon_end;
    return isMorning || isAfternoon;
  };

  // Drag and drop handlers
  const handleDragStart = (e, action) => {
    e.preventDefault();
    setDraggingAction(action);
    setIsDragging(true);
    setDragPosition({ x: e.clientX, y: e.clientY });
  };

  const handleDragMove = (e) => {
    if (!isDragging || !draggingAction) return;
    setDragPosition({ x: e.clientX, y: e.clientY });

    // Calculate preview time based on mouse position over timeline
    if (timelineRef.current && scrollContainerRef.current) {
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const relativeX = e.clientX - timelineRect.left + scrollLeft - MACHINE_LABEL_WIDTH;

      if (relativeX >= 0) {
        const hoursFromStart = relativeX / HOUR_WIDTH;
        const totalMinutes = Math.floor(hoursFromStart * 60);
        const roundedMinutes = Math.round(totalMinutes / 15) * 15;

        const timelineStart = getTimelineStart();
        const startTime = addMinutes(timelineStart, roundedMinutes);

        // Use production_time + setup_time for duration
        const durationMinutes = (draggingAction.production_time || 60) + (draggingAction.setup_time || 0);
        const endTime = addMinutes(startTime, durationMinutes);

        setDragPreviewTime({ start: startTime, end: endTime });
      }
    }
  };

  const handleDragEnd = async (e) => {
    if (!isDragging || !draggingAction) {
      setIsDragging(false);
      setDraggingAction(null);
      setDragPreviewTime(null);
      return;
    }

    // Determine which machine was dropped on
    if (timelineRef.current && scrollContainerRef.current) {
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const scrollTop = scrollContainerRef.current.scrollTop;

      const relativeX = e.clientX - timelineRect.left + scrollLeft - MACHINE_LABEL_WIDTH;
      const relativeY = e.clientY - timelineRect.top + scrollTop - TIMELINE_HEADER_HEIGHT;

      if (relativeX >= 0 && relativeY >= 0) {
        // Find target machine accounting for îlot headers
        let targetMachine = null;
        let currentY = 0;

        for (const group of machinesByIlot) {
          // Add îlot header height
          currentY += ILOT_HEADER_HEIGHT;

          for (const machine of group.machines) {
            if (relativeY >= currentY && relativeY < currentY + ROW_HEIGHT) {
              targetMachine = machine;
              break;
            }
            currentY += ROW_HEIGHT;
          }

          if (targetMachine) break;
        }

        if (targetMachine && dragPreviewTime) {
          // Plan the action
          try {
            const response = await fetch(`${API}/imported-actions/${draggingAction.imported_id}/plan`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                machine_id: targetMachine.machine_id,
                start_time: dragPreviewTime.start.toISOString(),
                end_time: dragPreviewTime.end.toISOString()
              })
            });

            if (response.ok) {
              const result = await response.json();
              toast.success(`Action planifiée sur ${targetMachine.name}`);
              // Mark as planned in local state
              setImportedActions(prev => prev.map(a =>
                a.imported_id === draggingAction.imported_id
                  ? { ...a, is_planned: true, planned_action_id: result.action_id }
                  : a
              ));
            } else {
              const error = await response.json();
              toast.error(error.detail || 'Erreur lors de la planification');
            }
          } catch (error) {
            toast.error('Erreur réseau');
          }
        }
      }
    }

    setIsDragging(false);
    setDraggingAction(null);
    setDragPreviewTime(null);
  };

  // Add/remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, draggingAction, dragPreviewTime]);

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h${mins}` : `${hours}h`;
    }
    return `${mins}min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="font-heading font-bold text-2xl uppercase tracking-tight">
            Import Planification
          </h1>
          <p className="text-muted-foreground font-mono text-sm">
            Importez des actions depuis Excel et planifiez-les par glisser-déposer
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
          <Button onClick={() => setUploadDialogOpen(true)} className="gap-2">
            <Upload className="w-4 h-4" />
            Importer Excel
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left panel - Imported actions */}
        <Card className="w-80 flex-shrink-0 border-2 flex flex-col">
          <CardHeader className="border-b-2 py-3 flex-shrink-0">
            <CardTitle className="font-heading uppercase tracking-tight text-sm flex items-center gap-2">
              <Package className="w-4 h-4" />
              Actions importées
            </CardTitle>
            <div className="flex gap-2 mt-2">
              <Badge variant={pendingCount > 0 ? "default" : "secondary"} className="text-xs">
                {pendingCount} à planifier
              </Badge>
              <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                {plannedCount} planifiée(s)
              </Badge>
            </div>
          </CardHeader>
          <div className="p-3 border-b space-y-2 flex-shrink-0">
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 text-sm"
            />
            <div className="flex gap-2">
              <Select value={showPlanned} onValueChange={setShowPlanned}>
                <SelectTrigger className="h-8 text-sm flex-1">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes ({importedActions.length})</SelectItem>
                  <SelectItem value="pending">À planifier ({pendingCount})</SelectItem>
                  <SelectItem value="planned">Planifiées ({plannedCount})</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                <SelectTrigger className="h-8 text-sm flex-1">
                  <SelectValue placeholder="Import" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous imports</SelectItem>
                  {batches.map(batch => (
                    <SelectItem key={batch.batch_id} value={batch.batch_id}>
                      {format(new Date(batch.created_at), 'dd/MM HH:mm', { locale: fr })} ({batch.pending})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-2">
              {filteredActions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune action à afficher</p>
                  <p className="text-xs mt-1">Importez un fichier Excel</p>
                </div>
              ) : (
                filteredActions.map((action) => (
                  <div
                    key={action.imported_id}
                    className={`
                      p-3 border-2 rounded-sm bg-card transition-colors
                      ${action.is_planned
                        ? 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20 opacity-75'
                        : 'cursor-grab active:cursor-grabbing hover:border-primary/50'
                      }
                      ${isDragging && draggingAction?.imported_id === action.imported_id ? 'opacity-50' : ''}
                    `}
                    onMouseDown={(e) => !action.is_planned && handleDragStart(e, action)}
                  >
                    <div className="flex items-start gap-2">
                      {action.is_planned ? (
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant={action.is_planned ? "default" : "outline"}
                            className={`text-xs font-mono ${action.is_planned ? 'bg-green-600' : ''}`}
                          >
                            N° OF: {action.of_number}
                          </Badge>
                          {action.is_planned && (
                            <span className="text-xs text-green-600 font-medium">Planifiée</span>
                          )}
                        </div>
                        <p className={`font-medium text-sm truncate ${action.is_planned ? 'text-muted-foreground' : ''}`}>
                          {action.title}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                          {action.article_code && (
                            <span className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {action.article_code}
                            </span>
                          )}
                          {action.quantity > 0 && (
                            <span>Qté: {action.quantity}</span>
                          )}
                          {(action.production_time > 0 || action.setup_time > 0) && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(action.production_time + action.setup_time)}
                            </span>
                          )}
                        </div>
                      </div>
                      {!action.is_planned && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAction(action.imported_id);
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Right panel - Planning grid */}
        <Card className="flex-1 border-2 flex flex-col min-w-0">
          <CardHeader className="border-b-2 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading uppercase tracking-tight text-sm flex items-center gap-2">
                <Factory className="w-4 h-4" />
                Vue 48H - Îlots / Machines
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedDate(addDays(selectedDate, -1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-mono text-sm min-w-[180px] text-center">
                  {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDate(new Date())}
                >
                  Aujourd'hui
                </Button>
              </div>
            </div>
          </CardHeader>

          <div className="flex-1 overflow-hidden relative" ref={timelineRef}>
            <ScrollArea className="h-full" ref={scrollContainerRef}>
              {/* Timeline grid */}
              <div
                className="relative"
                style={{
                  width: MACHINE_LABEL_WIDTH + (TOTAL_HOURS * HOUR_WIDTH),
                  minHeight: '100%'
                }}
              >
                {/* Hour headers */}
                <div className="sticky top-0 z-20 flex bg-background border-b-2">
                  <div
                    className="flex-shrink-0 border-r-2 bg-muted/50 flex items-center justify-center font-mono text-xs uppercase sticky left-0 z-30"
                    style={{ width: MACHINE_LABEL_WIDTH, height: TIMELINE_HEADER_HEIGHT }}
                  >
                    Machines
                  </div>
                  {HOURS.map((hour) => {
                    const dayOffset = Math.floor(hour / 24);
                    const hourInDay = hour % 24;
                    const isWorking = isWithinWorkingHours(hour, dayOffset);
                    const currentDate = addDays(selectedDate, dayOffset);

                    return (
                      <div
                        key={hour}
                        className={`
                          flex-shrink-0 border-r flex flex-col items-center justify-center
                          ${isWorking ? 'bg-primary/5' : 'bg-muted/30'}
                          ${hourInDay === 0 ? 'border-r-2' : ''}
                        `}
                        style={{ width: HOUR_WIDTH, height: TIMELINE_HEADER_HEIGHT }}
                      >
                        {hourInDay === 0 && (
                          <span className="text-[9px] font-mono text-muted-foreground">
                            {format(currentDate, 'EEE dd', { locale: fr })}
                          </span>
                        )}
                        <span className="font-mono text-xs">
                          {String(hourInDay).padStart(2, '0')}h
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Machine rows */}
                {machinesByIlot.map((group, groupIndex) => (
                  <div key={group.ilot.ilot_id}>
                    {/* Ilot header */}
                    <div
                      className="flex bg-muted/80 border-b-2"
                      style={{ height: ILOT_HEADER_HEIGHT }}
                    >
                      <div
                        className="font-mono text-xs uppercase tracking-wider px-3 flex items-center gap-2 bg-muted/80 sticky left-0 z-10"
                        style={{ width: MACHINE_LABEL_WIDTH }}
                      >
                        <Factory className="w-3 h-3" />
                        {group.ilot.name}
                      </div>
                      <div
                        style={{ width: TOTAL_HOURS * HOUR_WIDTH }}
                        className="bg-muted/50"
                      />
                    </div>

                    {/* Machines */}
                    {group.machines.map((machine, machineIndex) => (
                      <div
                        key={machine.machine_id}
                        className="flex border-b hover:bg-muted/20 transition-colors"
                        style={{ height: ROW_HEIGHT }}
                      >
                        {/* Machine label */}
                        <div
                          className="flex-shrink-0 border-r-2 px-3 flex items-center gap-2 bg-background sticky left-0 z-10"
                          style={{ width: MACHINE_LABEL_WIDTH }}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{machine.name}</p>
                            <p className="text-xs text-muted-foreground font-mono truncate">
                              {machine.type}
                            </p>
                          </div>
                        </div>

                        {/* Time slots */}
                        <div className="flex relative">
                          {HOURS.map((hour) => {
                            const dayOffset = Math.floor(hour / 24);
                            const hourInDay = hour % 24;
                            const isWorking = isWithinWorkingHours(hour, dayOffset);

                            return (
                              <div
                                key={hour}
                                className={`
                                  flex-shrink-0 border-r
                                  ${isWorking ? 'bg-primary/5' : 'bg-muted/10'}
                                  ${hourInDay === 0 ? 'border-r-2' : ''}
                                `}
                                style={{ width: HOUR_WIDTH, height: ROW_HEIGHT }}
                              />
                            );
                          })}

                          {/* Preview when dragging */}
                          {isDragging && draggingAction && dragPreviewTime && (() => {
                            // Calculate Y position for current machine accounting for îlot headers
                            if (scrollContainerRef.current && timelineRef.current) {
                              const timelineRect = timelineRef.current.getBoundingClientRect();
                              const scrollTop = scrollContainerRef.current.scrollTop;
                              const relativeY = dragPosition.y - timelineRect.top + scrollTop - TIMELINE_HEADER_HEIGHT;

                              // Calculate Y bounds for this machine
                              let machineYStart = 0;
                              let found = false;
                              for (const g of machinesByIlot) {
                                machineYStart += ILOT_HEADER_HEIGHT; // îlot header
                                for (const m of g.machines) {
                                  if (m.machine_id === machine.machine_id) {
                                    found = true;
                                    break;
                                  }
                                  machineYStart += ROW_HEIGHT;
                                }
                                if (found) break;
                              }

                              const isHovered = relativeY >= machineYStart && relativeY < machineYStart + ROW_HEIGHT;

                              if (isHovered) {
                                const timelineStart = getTimelineStart();
                                const startOffset = (dragPreviewTime.start - timelineStart) / (1000 * 60 * 60) * HOUR_WIDTH;
                                const endOffset = (dragPreviewTime.end - timelineStart) / (1000 * 60 * 60) * HOUR_WIDTH;
                                const width = endOffset - startOffset;

                                return (
                                  <div
                                    className="absolute top-1 bottom-1 bg-primary/30 border-2 border-dashed border-primary rounded-sm pointer-events-none"
                                    style={{
                                      left: startOffset,
                                      width: Math.max(width, 30)
                                    }}
                                  >
                                    <div className="absolute -top-6 left-0 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded font-mono whitespace-nowrap">
                                      {format(dragPreviewTime.start, 'HH:mm')} - {format(dragPreviewTime.end, 'HH:mm')}
                                    </div>
                                  </div>
                                );
                              }
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                {machinesByIlot.length === 0 && (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    <div className="text-center">
                      <Factory className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucune machine configurée</p>
                      <p className="text-sm">Créez des îlots et des machines pour commencer</p>
                    </div>
                  </div>
                )}
              </div>
              <ScrollBar orientation="horizontal" />
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          </div>
        </Card>
      </div>

      {/* Dragging ghost */}
      {isDragging && draggingAction && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: dragPosition.x + 10,
            top: dragPosition.y + 10
          }}
        >
          <div className="bg-primary text-primary-foreground p-3 rounded-sm shadow-lg max-w-xs">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="text-xs">
                N° OF: {draggingAction.of_number}
              </Badge>
            </div>
            <p className="font-medium text-sm truncate">{draggingAction.title}</p>
            {dragPreviewTime && (
              <p className="text-xs mt-1 font-mono">
                {format(dragPreviewTime.start, 'HH:mm')} - {format(dragPreviewTime.end, 'HH:mm')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Importer un fichier Excel
            </DialogTitle>
            <DialogDescription>
              Importez vos actions depuis un fichier Excel (.xlsx)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-sm p-8 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Glissez votre fichier ici ou cliquez pour sélectionner
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="excel-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Sélectionner un fichier
                  </>
                )}
              </Button>
            </div>

            <div className="bg-muted/50 rounded-sm p-4">
              <p className="font-mono text-xs uppercase tracking-wider mb-2 text-muted-foreground">
                Colonnes attendues
              </p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <span>N° OF</span><span className="text-muted-foreground">Ordre de Fabrication *</span>
                <span>Libellé action</span><span className="text-muted-foreground">Titre de l'action *</span>
                <span>N° Etiq fab</span><span className="text-muted-foreground">Numéro étiquette</span>
                <span>N° action</span><span className="text-muted-foreground">Numéro d'action</span>
                <span>Code article</span><span className="text-muted-foreground">Code de l'article</span>
                <span>Qté lancée</span><span className="text-muted-foreground">Quantité</span>
                <span>Section</span><span className="text-muted-foreground">Section atelier</span>
                <span>Code resource</span><span className="text-muted-foreground">Code ressource</span>
                <span>Code action</span><span className="text-muted-foreground">Code unique</span>
                <span>Date début</span><span className="text-muted-foreground">Date prévue</span>
                <span>tps de réglage</span><span className="text-muted-foreground">Minutes réglage</span>
                <span>tps total de production</span><span className="text-muted-foreground">Minutes production</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">* Colonnes obligatoires</p>
            </div>

            {batches.length > 0 && (
              <div className="border-t pt-4">
                <p className="font-mono text-xs uppercase tracking-wider mb-2 text-muted-foreground">
                  Imports précédents
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {batches.map(batch => (
                    <div
                      key={batch.batch_id}
                      className="flex items-center justify-between p-2 bg-muted/30 rounded-sm text-sm"
                    >
                      <div>
                        <span className="font-mono">{format(new Date(batch.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}</span>
                        <span className="text-muted-foreground ml-2">
                          {batch.planned}/{batch.total} planifiées
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteBatch(batch.batch_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog with editable table */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Aperçu et modification des données
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les lignes à importer et modifiez les données si nécessaire. Cliquez sur une cellule pour la modifier.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={previewData.length > 0 && previewData.every(row => row.selected)}
                  onCheckedChange={handleToggleAll}
                />
                <span className="text-sm text-muted-foreground">
                  Tout sélectionner
                </span>
              </div>
              <Badge variant="outline">
                {previewData.filter(r => r.selected).length} / {previewData.length} sélectionnée(s)
              </Badge>
            </div>
          </div>

          <ScrollArea className="flex-1 border rounded-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-10 sticky left-0 bg-muted/50"></TableHead>
                  <TableHead className="font-mono text-xs min-w-[80px]">N° OF *</TableHead>
                  <TableHead className="font-mono text-xs min-w-[150px]">Libellé *</TableHead>
                  <TableHead className="font-mono text-xs min-w-[100px]">Code article</TableHead>
                  <TableHead className="font-mono text-xs min-w-[60px]">Qté</TableHead>
                  <TableHead className="font-mono text-xs min-w-[80px]">Section</TableHead>
                  <TableHead className="font-mono text-xs min-w-[80px]">Code resource</TableHead>
                  <TableHead className="font-mono text-xs min-w-[60px]">Réglage</TableHead>
                  <TableHead className="font-mono text-xs min-w-[60px]">Production</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewData.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className={`${!row.selected ? 'opacity-50 bg-muted/20' : ''} ${
                      !row.of_number || !row.title ? 'bg-destructive/10' : ''
                    }`}
                  >
                    <TableCell className="sticky left-0 bg-background">
                      <Checkbox
                        checked={row.selected}
                        onCheckedChange={() => handleToggleRow(rowIndex)}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'of_number', row.of_number)}
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px]">
                      {renderEditableCell(row, rowIndex, 'title', row.title)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'article_code', row.article_code)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'quantity', row.quantity)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'section', row.section)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'resource_code', row.resource_code)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'setup_time', row.setup_time ? `${row.setup_time}min` : '')}
                    </TableCell>
                    <TableCell className="text-xs">
                      {renderEditableCell(row, rowIndex, 'production_time', row.production_time ? `${row.production_time}min` : '')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <DialogFooter className="flex items-center justify-between border-t pt-4">
            <div className="text-sm text-muted-foreground">
              {previewData.some(row => !row.of_number || !row.title) && (
                <span className="text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Certaines lignes ont des champs obligatoires manquants
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={uploading || previewData.filter(r => r.selected).length === 0}
                className="gap-2"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Importer {previewData.filter(r => r.selected).length} ligne(s)
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
